import { defineStore } from "pinia";
import { ref } from "vue";
import { logger } from "../infrastructure/services/LoggerService";
import { AVAILABLE_LANGUAGE_TAGS } from "@/domain/types";
import { DEFAULT_LANGUAGE_TAG, getLanguageCode, isLanguageTagSupported, isNormalizedLanguageTag, normalizeLocale, } from "@/shared/utils/localeUtils.ts";
export const useLanguageStore = defineStore("language", () => {
    // State
    const currentLocale = ref(DEFAULT_LANGUAGE_TAG);
    const source = ref("default");
    const isInitialized = ref(false);
    // Getters
    // Actions
    const setLocale = (locale, localeSource = "default") => {
        if (!isLanguageTagSupported(locale)) {
            // Since locale is a LanguageTags type, this should not happen unless forced cast is used
            logger.debug(`Locale ${locale} is not supported. Using default '${DEFAULT_LANGUAGE_TAG}'.`);
            locale = DEFAULT_LANGUAGE_TAG;
        }
        currentLocale.value = locale;
        source.value = localeSource;
        isInitialized.value = true;
        // Save to localStorage for persistence
        localStorage.setItem("locale", locale);
        localStorage.setItem("localeSource", localeSource);
        logger.debug(`Language store updated: ${locale} (source: ${localeSource})`);
    };
    const initializeFromStorage = () => {
        const storedLocale = localStorage.getItem("locale");
        const storedSource = localStorage.getItem("localeSource");
        // 1. Check localStorage (user preference)
        if (storedLocale && isNormalizedLanguageTag(storedLocale)) {
            currentLocale.value = storedLocale;
            source.value = storedSource || "localStorage";
            isInitialized.value = true;
            logger.debug(`Language store initialized from storage: ${storedLocale} (source: ${source.value})`);
            return;
        }
        // 2. Try to match browser locale to our supported locales
        const browserLanguage = navigator.language;
        if (browserLanguage && isNormalizedLanguageTag(browserLanguage)) {
            setLocale(browserLanguage, "browser");
            return;
        }
        // 3. Try to match a base language from browser locale (e.g., "fr" from "fr-CA")
        const languageCode = getLanguageCode(browserLanguage);
        const normalizedFromCode = normalizeLocale(languageCode);
        if (normalizedFromCode && isLanguageTagSupported(normalizedFromCode)) {
            setLocale(normalizedFromCode, "browser");
            return;
        }
        // 4. Default fallback
        setLocale(DEFAULT_LANGUAGE_TAG, "default");
    };
    const setLocaleFromKeycloak = (keycloakLocale) => {
        if (!keycloakLocale) {
            // Use debug level to avoid confusion in browser console
            logger.debug(`Keycloak locale ${keycloakLocale || "undefined"} not available or not supported, keeping current locale`);
            return false;
        }
        // Normalize Keycloak locale (e.g., "fr" → "fr-FR")
        const normalizedLocale = normalizeLocale(keycloakLocale);
        // Keycloak locale has the highest priority
        logger.debug(`Setting locale from Keycloak: ${keycloakLocale} → ${normalizedLocale}`);
        setLocale(normalizedLocale, "keycloak");
        return true;
    };
    const setLocaleFromApi = (apiLocale) => {
        if (apiLocale) {
            // Normalize API locale (e.g., "fr" → "fr-FR")
            const normalizedLocale = normalizeLocale(apiLocale);
            // Only set API locale if we don't already have a Keycloak locale
            // API locale has higher priority than localStorage but lower than Keycloak
            if (source.value !== "keycloak") {
                logger.debug(`Setting locale from API: ${apiLocale} → ${normalizedLocale}`);
                setLocale(normalizedLocale, "api");
                return true;
            }
            else {
                logger.debug(`API locale available but keeping Keycloak locale (higher priority)`);
                return false;
            }
        }
        else {
            logger.debug(`API locale not available, keeping current locale`);
            return false;
        }
    };
    const reset = () => {
        currentLocale.value = DEFAULT_LANGUAGE_TAG;
        source.value = "default";
        isInitialized.value = false;
        localStorage.removeItem("locale");
        localStorage.removeItem("localeSource");
        logger.debug("Language store reset to default");
    };
    return {
        // State
        currentLocale,
        source,
        isInitialized,
        // Getters
        // Actions
        setLocale,
        initializeFromStorage,
        setLocaleFromKeycloak,
        setLocaleFromApi,
        reset,
        // Utils
        availableLocales: AVAILABLE_LANGUAGE_TAGS,
        isLanguageTagSupported,
    };
});
